import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Globe, Shield, Clock, MapPin, ArrowRight, Check } from 'lucide-react';
import { Badge } from '../components/ui/badge';
import { getActiveVisas, getActiveCountries } from '../services/api';
import { useCurrency } from '../context/CurrencyContext';

export default function VisasPage() {
  const [visas, setVisas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeRegion, setActiveRegion] = useState('all');
  const [activeCountries, setActiveCountries] = useState([]);
  const { formatPrice } = useCurrency();

  const regions = ['all', 'Europe', 'Middle East', 'Americas', 'Asia', 'Africa'];

  useEffect(() => {
    getActiveCountries().then(data => setActiveCountries(data.countries || [])).catch(() => {});
  }, []);

  useEffect(() => {
    const fetchVisas = async () => {
      setLoading(true);
      try {
        const data = await getActiveVisas();
        const filtered = activeRegion !== 'all' 
          ? data.filter(v => v.region === activeRegion)
          : data;
        setVisas(filtered);
      } catch (error) {
        console.error('Error fetching visas:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchVisas();
  }, [activeRegion]);

  return (
    <div className="min-h-screen bg-[#0B0C10] pt-20" data-testid="visas-page">
      {/* Hero */}
      <section className="relative py-24 overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://images.pexels.com/photos/29402988/pexels-photo-29402988.jpeg"
            alt="Visa programs"
            className="w-full h-full object-cover opacity-30"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-[#0B0C10] via-[#0B0C10]/80 to-[#0B0C10]" />
        </div>

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <span className="label-overline">Residency Programs</span>
            <h1 className="text-4xl sm:text-5xl text-white mt-4 mb-4">
              Visa & Residency Pathways
            </h1>
            <p className="text-[#A1A1AA] max-w-2xl mx-auto">
              Explore comprehensive guide to golden visa and residency-by-investment programs 
              across our partner countries. Each program offers unique benefits for Indian investors.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Region Filters */}
      <section className="border-b border-[#2A2F3A]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex overflow-x-auto py-4 space-x-4 scrollbar-hide">
            {regions.map((region) => (
              <button
                key={region}
                onClick={() => setActiveRegion(region)}
                className={`px-6 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-colors ${
                  activeRegion === region
                    ? 'bg-[#D4AF37] text-[#0B0C10]'
                    : 'bg-[#1A1D24] text-white hover:bg-[#2A2F3A]'
                }`}
                data-testid={`region-filter-${region.toLowerCase().replace(' ', '-')}`}
              >
                {region === 'all' ? 'All Regions' : region}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Visa Programs */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {loading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              {[1, 2, 3, 4].map((i) => (
                <div key={i} className="bg-[#1A1D24] rounded-xl h-80 animate-pulse" />
              ))}
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              {visas.map((visa, index) => (
                <motion.div
                  key={visa.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.1 }}
                >
                  <Link
                    to={`/visas/${visa.id}`}
                    className="block group bg-[#1A1D24] border border-[#2A2F3A] rounded-xl overflow-hidden hover:border-[#D4AF37]/50 transition-all hover:-translate-y-1"
                    data-testid={`visa-card-${visa.id}`}
                  >
                    {/* Image */}
                    <div className="relative h-48 image-zoom">
                      <img
                        src={visa.image}
                        alt={visa.name}
                        className="w-full h-full object-cover"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
                      <div className="absolute bottom-4 left-4 right-4">
                        <Badge className="bg-[#D4AF37] text-[#0B0C10]">
                          {visa.type}
                        </Badge>
                      </div>
                    </div>

                    {/* Content */}
                    <div className="p-6">
                      <div className="flex items-start justify-between mb-4">
                        <div>
                          <h3 className="text-xl text-white font-medium group-hover:text-[#D4AF37] transition-colors">
                            {visa.name}
                          </h3>
                          <div className="flex items-center text-[#A1A1AA] text-sm mt-1">
                            <MapPin className="h-4 w-4 mr-1" />
                            {visa.country} • {visa.region}
                          </div>
                        </div>
                        <div className="text-right">
                          <div className="text-[#A1A1AA] text-xs uppercase tracking-wider">From</div>
                          <div className="text-[#D4AF37] font-semibold">
                            {formatPrice(visa.min_investment)}
                          </div>
                        </div>
                      </div>

                      {/* Quick Info */}
                      <div className="grid grid-cols-2 gap-4 py-4 border-t border-[#2A2F3A]">
                        <div className="flex items-center text-sm text-[#A1A1AA]">
                          <Clock className="h-4 w-4 mr-2 text-[#D4AF37]" />
                          {visa.processing_time}
                        </div>
                        <div className="flex items-center text-sm text-[#A1A1AA]">
                          <Shield className="h-4 w-4 mr-2 text-[#D4AF37]" />
                          {visa.validity}
                        </div>
                      </div>

                      {/* Benefits Preview */}
                      <div className="space-y-2 mt-4">
                        {visa.benefits.slice(0, 3).map((benefit, i) => (
                          <div key={i} className="flex items-start text-sm text-[#A1A1AA]">
                            <Check className="h-4 w-4 text-[#D4AF37] mr-2 flex-shrink-0 mt-0.5" />
                            <span className="line-clamp-1">{benefit}</span>
                          </div>
                        ))}
                      </div>

                      {/* Link */}
                      <div className="flex items-center justify-end mt-6 text-[#D4AF37] group-hover:translate-x-1 transition-transform">
                        <span className="text-sm">Learn More</span>
                        <ArrowRight className="h-4 w-4 ml-1" />
                      </div>
                    </div>
                  </Link>
                </motion.div>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Why Residency by Investment */}
      <section className="py-24 bg-[#1A1D24]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <span className="label-overline">Benefits</span>
            <h2 className="text-3xl sm:text-4xl text-white mt-4 mb-4">
              Why Choose Residency by Investment?
            </h2>
            <p className="text-[#A1A1AA] max-w-2xl mx-auto">
              Global mobility, financial security, and a better quality of life for your family.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {[
              { icon: Globe, title: 'Global Mobility', desc: 'Visa-free travel to 100+ countries with select residency programs' },
              { icon: Shield, title: 'Asset Protection', desc: 'Diversify your wealth across stable international jurisdictions' },
              { icon: 'education', title: 'Education Access', desc: 'World-class education opportunities for your children' },
              { icon: 'health', title: 'Healthcare', desc: 'Access to premium healthcare systems worldwide' },
            ].map((item, index) => (
              <motion.div
                key={item.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
                viewport={{ once: true }}
                className="text-center p-6"
              >
                <div className="w-16 h-16 rounded-full bg-[#D4AF37]/10 flex items-center justify-center mx-auto mb-4">
                  {item.icon === 'education' ? (
                    <svg className="h-8 w-8 text-[#D4AF37]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 14l9-5-9-5-9 5 9 5z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 14l6.16-3.422a12.083 12.083 0 01.665 6.479A11.952 11.952 0 0012 20.055a11.952 11.952 0 00-6.824-2.998 12.078 12.078 0 01.665-6.479L12 14z" />
                    </svg>
                  ) : item.icon === 'health' ? (
                    <svg className="h-8 w-8 text-[#D4AF37]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
                    </svg>
                  ) : (
                    <item.icon className="h-8 w-8 text-[#D4AF37]" />
                  )}
                </div>
                <h3 className="text-lg text-white font-medium mb-2">{item.title}</h3>
                <p className="text-[#A1A1AA] text-sm">{item.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 bg-[#0B0C10]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-2xl sm:text-3xl text-white mb-4">
            Ready to Explore Your Options?
          </h2>
          <p className="text-[#A1A1AA] mb-8">
            Book a free consultation with our residency experts to discuss the best 
            program for your family's needs.
          </p>
          <Link to="/contact">
            <Button className="btn-luxury px-8" data-testid="visa-cta-btn">
              Schedule Consultation <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
}

// Import Button component
import { Button } from '../components/ui/button';
